import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { submitContact } from "@/lib/contact.functions";
import { reviseHeadline, type HeadlineResult } from "@/lib/headline.functions";
import { StickyHeader } from "@/components/site/StickyHeader";
import { Footer } from "@/components/site/Footer";

export const Route = createFileRoute("/headline")({
  head: () => ({
    meta: [
      {
        title: "Free LinkedIn Headline Reviser for Financial Advisors",
      },
      {
        name: "description",
        content:
          "Paste your LinkedIn headline and get three clearer, referral-ready rewrites in seconds. Built for advisors, RIAs, and financial professionals.",
      },
      { property: "og:title", content: "Free LinkedIn Headline Reviser for Financial Advisors" },
      {
        property: "og:description",
        content:
          "Instant, compliance-aware LinkedIn headline rewrites for financial professionals. No call required.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { rel: "canonical", content: "https://katiefernands.com/headline" },
    ],
    links: [{ rel: "canonical", href: "https://katiefernands.com/headline" }],
  }),
  component: HeadlinePage,
});

const gateSchema = z.object({
  name: z.string().trim().min(1, "Please add your first name").max(120),
  email: z.string().trim().email("Enter a valid email").max(254),
});

function HeadlinePage() {
  const [email, setEmail] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  return (
    <div className="bg-background text-foreground min-h-screen flex flex-col">
      <StickyHeader />
      <main className="flex-1 pt-32 pb-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          {!unlocked ? (
            <Gate onUnlock={(email) => { setEmail(email); setUnlocked(true); }} />
          ) : (
            <Tool email={email} />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Gate({ onUnlock }: { onUnlock: (email: string) => void }) {
  const send = useServerFn(submitContact);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subscribe, setSubscribe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = gateSchema.safeParse({ name, email });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Check your details");
      return;
    }
    setSubmitting(true);
    try {
      await send({
        data: {
          name: parsed.data.name,
          email: parsed.data.email,
          message: subscribe
            ? "Headline reviser: add me to the list"
            : "Headline reviser: tool access only",
          source: "headline-reviser",
        },
      });
    } catch {
      // Never block access to the tool on a capture failure.
    } finally {
      setSubmitting(false);
      onUnlock(parsed.data.email);
    }
  }

  return (
    <div className="mx-auto max-w-lg">
      <p className="text-xs uppercase tracking-[0.24em] text-primary mb-3">
        Free tool
      </p>
      <h1 className="text-3xl md:text-4xl font-serif tracking-tight">
        Fix your LinkedIn headline in about 30 seconds.
      </h1>
      <p className="mt-4 text-muted-foreground leading-relaxed">
        Most advisor headlines read like a business card. Paste yours and get three
        rewrites built around who you serve and the outcome you deliver, so the right
        people can find you and refer you.
      </p>
      <form onSubmit={handleSubmit} className="mt-8 space-y-4">
        <input
          type="text"
          placeholder="Your first name"
          value={name}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-full border border-border/60 bg-background px-5 py-3 text-sm focus:border-primary focus:outline-none"
        />
        <input
          type="email"
          placeholder="you@firm.com"
          value={email}
          maxLength={254}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-full border border-border/60 bg-background px-5 py-3 text-sm focus:border-primary focus:outline-none"
        />
        <label className="flex items-start gap-3 text-sm text-muted-foreground px-1">
          <input
            type="checkbox"
            checked={subscribe}
            onChange={(e) => setSubscribe(e.target.checked)}
            className="mt-1 accent-primary"
          />
          <span>
            Add me to the list. Occasional notes on referrals and visibility for
            financial professionals. Unsubscribe anytime.
          </span>
        </label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? "Opening the tool…" : "Revise my headline"}
        </button>
      </form>
    </div>
  );
}

const initialForm = {
  current_headline: "",
  role: "",
  audience: "",
  outcome: "",
  credentials: "",
};

function Tool({ email }: { email: string }) {
  const revise = useServerFn(reviseHeadline);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<HeadlineResult | null>(null);
  const [copied, setCopied] = useState<number | null>(null);

  function set(key: keyof typeof initialForm, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.current_headline.trim()) {
      setError("Paste your current headline first.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      setResult(await revise({ data: { ...form, email } }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  }

  async function copy(text: string, index: number) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(index);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setCopied(null);
    }
  }

  return (
    <div>
      <p className="text-xs uppercase tracking-[0.24em] text-primary mb-3">
        LinkedIn headline reviser
      </p>
      <h1 className="text-3xl md:text-4xl font-serif tracking-tight">
        Paste your headline. Get three better versions.
      </h1>

      <form onSubmit={handleSubmit} className="mt-8 space-y-5">
        <Field label="Your current LinkedIn headline" required>
          <textarea
            rows={3}
            maxLength={400}
            value={form.current_headline}
            onChange={(e) => set("current_headline", e.target.value)}
            placeholder="Financial Advisor at Example Wealth Management"
            className="w-full rounded-2xl border border-border/60 bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none"
          />
        </Field>
        <div className="grid gap-5 md:grid-cols-2">
          <Field label="Your role or title">
            <Input value={form.role} onChange={(v) => set("role", v)} max={200} placeholder="Wealth advisor, RIA founder" />
          </Field>
          <Field label="Who you serve">
            <Input value={form.audience} onChange={(v) => set("audience", v)} max={300} placeholder="Business owners nearing exit" />
          </Field>
          <Field label="The outcome you deliver">
            <Input value={form.outcome} onChange={(v) => set("outcome", v)} max={300} placeholder="Retirement income they don't worry about" />
          </Field>
          <Field label="Credentials or firm (optional)">
            <Input value={form.credentials} onChange={(v) => set("credentials", v)} max={200} placeholder="CFP®, Example Wealth" />
          </Field>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="rounded-full bg-primary text-primary-foreground px-8 py-3 text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {loading ? "Revising…" : "Revise my headline"}
        </button>
        <p className="text-xs text-muted-foreground">
          This is a draft. Validate any suggested headline or profile language with your firm's compliance team before publishing it.
        </p>
      </form>

      {result && (
        <section className="mt-14 space-y-8">
          <div className="rounded-2xl border border-border/60 bg-muted/30 p-6">
            <h2 className="font-serif text-xl">What your headline is doing now</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {result.diagnosis}
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="font-serif text-2xl">Three rewrites</h2>
            {result.options?.map((opt, i) => (
              <article key={i} className="rounded-2xl border border-border/60 p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-primary">
                  {opt.label}
                </p>
                <p className="mt-3 text-lg leading-snug">{opt.headline}</p>
                <p className="mt-3 text-sm text-muted-foreground">{opt.why}</p>
                <button
                  type="button"
                  onClick={() => copy(opt.headline, i)}
                  className="mt-4 rounded-full border border-border/60 px-4 py-2 text-xs font-medium hover:border-primary"
                >
                  {copied === i ? "Copied" : "Copy headline"}
                </button>
              </article>
            ))}
          </div>

          {result.quick_wins?.length > 0 && (
            <div>
              <h2 className="font-serif text-2xl">Three more quick fixes</h2>
              <ul className="mt-4 space-y-3">
                {result.quick_wins.map((win, i) => (
                  <li key={i} className="flex gap-3 text-sm leading-relaxed">
                    <span className="text-primary">{i + 1}.</span>
                    <span>{win}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-2xl bg-primary/5 border border-primary/20 p-6">
            <p className="text-sm leading-relaxed">
              A headline gets you found. A referral system gets you introduced. If you
              want the second part, book a 30 minute conversation. No pitch, just clarity.
            </p>
            <a
              href="https://calendly.com/katie-fernands/discovery-call"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-block rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:opacity-90"
            >
              Book a discovery call
            </a>
          </div>
        </section>
      )}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium">
        {label}
        {required && <span className="text-primary"> *</span>}
      </span>
      {children}
    </label>
  );
}

function Input({
  value,
  onChange,
  max,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  max: number;
  placeholder: string;
}) {
  return (
    <input
      type="text"
      value={value}
      maxLength={max}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-full border border-border/60 bg-background px-5 py-3 text-sm focus:border-primary focus:outline-none"
    />
  );
}
